"use client";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  addCouponCode,
  clearCouponCode,
  couponCode,
  decreaseQuantity,
  eventTicketForCurrentEvent,
  getTicketDialogVisibility,
  increaseQuantity,
  isCouponCodeAdded,
  loadEventTickets,
  loginToContinue,
  selectTicketPageContinue,
  setCouponCode,
  totalNumberOfTickets,
} from "@/app/events/[id]/store/buy-tickets.store";
import { IEventTicketCategoryForBuy } from "@/app/dashboard/models/EventTicketCategory";
import { useSignals } from "@preact/signals-react/runtime";
import {
  CheckCircle,
  CircleOff,
  Minus,
  Plus,
  TriangleAlert,
} from "lucide-react";
import { CardContent, CardFooter } from "@/components/ui/card";
import { StepList } from "./StepList";
import { isLoggedIn } from "@/store/auth.store";
import { eventInSingleEventPage } from "@/app/events/store/single-page.store";
import { useParams } from "next/navigation";
import { useEffect, useMemo } from "react";
import { SeatArrangement } from "@/app/events/[id]/components/seating/SeatArrangement";
import { EventStatus } from "@/app/dashboard/models/Event";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const SelectTickets = () => {
  useSignals();
  const { id: eventId } = useParams();
  const id = eventId || eventInSingleEventPage.value?.id;
  const isCouponAdded = isCouponCodeAdded.value;
  const ticketsAdded = eventTicketForCurrentEvent?.value?.some(
    (item) => item.quantity >= 1
  );

  useEffect(() => {
    loadEventTickets(id as string);
  }, [id]);

  const event = eventInSingleEventPage.value;
  const isEventPublished = useMemo(
    () => event?.status === EventStatus.Published,
    [event?.status]
  );
  const isDraft = useMemo(
    () => event?.status === EventStatus.Draft,
    [event?.status]
  );
  return (
    <>
      <CardContent className="grow overflow-y-auto pb-0">
        <div className="pt-6 mb-6">
          <StepList />
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2 flex gap-2 items-center">
            Enter promo code
            {isCouponAdded && (
              <span className="text-mint-green">
                <CheckCircle />
              </span>
            )}
          </h3>

          <form
            className="flex"
            onSubmit={async (event) => {
              event.preventDefault();
              event.stopPropagation();
              await addCouponCode();
            }}
          >
            <Input
              placeholder="Promo code"
              className="mr-2"
              aria-label="Promo code"
              onChange={(e) => setCouponCode(e.target.value)}
              disabled={isCouponAdded}
              value={couponCode.value}
            />
            {isCouponAdded ? (
              <Button
                variant="outline"
                type="reset"
                onClick={() => clearCouponCode()}
              >
                Clear
              </Button>
            ) : (
              <Button type="submit">Apply</Button>
            )}
          </form>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-2">
            Available ticket categories
          </h3>
          {event.isSeatingPublished ? (
            <>
              <SeatArrangement />
            </>
          ) : eventTicketForCurrentEvent?.value?.length === 0 ? (
            <div className="flex flex-col min-h-[120px] justify-center items-center gap-3">
              <CircleOff
                size={32}
                strokeWidth={1.5}
                className="stroke-stone-neutral-11"
              />
              <p className="m-0 text-base text-stone-neutral-11">
                Looks like no tickets are added to this event yet
              </p>
            </div>
          ) : (
            eventTicketForCurrentEvent?.value?.map((item) => (
              <TicketCategory {...item} event={event} key={item.id} />
            ))
          )}
        </div>
      </CardContent>
      <CardFooter className="justify-end pt-6 border-t absolute bottom-0 md:static bg-white w-full md:w-auto">
        <div className="flex justify-between gap-4">
          {/* TODO: close the modal when click on the cancel button  */}
          <Button variant="outline" onClick={getTicketDialogVisibility}>
            Cancel
          </Button>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                {!isEventPublished || !ticketsAdded || isDraft ? (
                  <Button className="bg-navy-blue hover:bg-navy-blue/85">
                    Continue
                  </Button>
                ) : (
                  <Button
                    className="bg-navy-blue hover:bg-navy-blue/85"
                    onClick={() =>
                      isLoggedIn.value
                        ? selectTicketPageContinue()
                        : loginToContinue()
                    }
                  >
                    Continue
                  </Button>
                )}
              </TooltipTrigger>
              {(!isEventPublished || !ticketsAdded || isDraft) && (
                <TooltipContent className="bg-black text-white">
                  {!isEventPublished && !isDraft
                    ? "You can get tickets only after the event is published."
                    : isDraft
                    ? "Ticket sales are not available while the event is under review."
                    : "Please select at least one ticket to continue."}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardFooter>
    </>
  );
};

const TicketCategory = ({
  title,
  price,
  description,
  id,
  quantity,
  maxTicketCount,
  ticketCount,
  currencyFormat,
  event,
  tempCount,
}: IEventTicketCategoryForBuy) => {
  useSignals();

  const totalTicketCount = totalNumberOfTickets.value;

  return (
    <div className="flex flex-col gap-2 border border-stone-neutral-5 rounded-lg p-4 mb-4">
      <div className="flex justify-between items-center gap-3">
        <h4 className="font-semibold">{title}</h4>
        {maxTicketCount - ticketCount - tempCount > 0 ? (
          <div className="flex items-center">
            <Button
              disabled={quantity === 0}
              onClick={() => decreaseQuantity(id)}
              variant="outline"
              size="icon"
              className="h-8 w-8 rounded-full"
            >
              <span className="sr-only">Decrease quantity for {title}</span>
              <span aria-hidden="true">
                <Minus className="w-4" />
              </span>
            </Button>
            <span className="mx-3" aria-label={`Quantity for ${title}`}>
              {quantity}
            </span>
            <Button
              onClick={() => increaseQuantity(id)}
              disabled={
                quantity + ticketCount + tempCount >= maxTicketCount ||
                (event.maxTicketLimit > 0 &&
                  totalTicketCount >= event.maxTicketLimit)
              }
              variant="outline"
              size="icon"
              className="h-8 w-8 rounded-full"
            >
              <span className="sr-only">Increase quantity for {title}</span>
              <span aria-hidden="true">
                <Plus className="w-4" />
              </span>
            </Button>
          </div>
        ) : (
          <div className="flex gap-1 items-center py-1 px-2 rounded-lg bg-lemon-yellow-2 border border-lemon-yellow-3">
            <TriangleAlert
              strokeWidth={1.5}
              size={20}
              className="stroke-lemon-yellow-12"
            />
            <div className="text-base text-lemon-yellow-12">
              Tickets sold out
            </div>
          </div>
        )}
      </div>
      {maxTicketCount - ticketCount - tempCount > 0 && (
        <div className="flex justify-between items-center text-sm">
          <span className="text-muted-foreground max-w-[70%]">
            {description}
          </span>
          <span className="font-semibold">
            {currencyFormat} {price.toFixed(2)}
          </span>
        </div>
      )}
    </div>
  );
};
